/**
 * 도메인별 API 호출 함수
 *
 * Phase 1에서 사용하는 모든 API를 타입 안전하게 호출합니다.
 */

import { apiGet, apiPost } from './client';
import type {
  ApiResponse,
  InitData,
  BalanceData,
  RecentCampaignsData,
  ResendData,
  FeedbackRequestPayload,
  FeedbackData,
} from '@/types/api';

/**
 * 챗봇 초기화 - 인증 상태 + 사용자 정보 + 페이지 컨텍스트
 */
export function fetchInit(currentPage: string): Promise<ApiResponse<InitData>> {
  const params = new URLSearchParams({ page: currentPage });
  return apiGet<InitData>(`/chatbot/api/init.jsp?${params.toString()}`);
}

/**
 * 잔액 조회 (로그인 필수)
 *
 * dispatcher.jsp의 `balance` case로 라우팅된다. 응답 스키마는 BalanceData 참조.
 * 비로그인 시 401 + meta.login_url, 정지 계정은 403 + meta.support_phone.
 */
export function fetchBalance(): Promise<ApiResponse<BalanceData>> {
  return apiGet<BalanceData>('/chatbot/api/balance');
}

/**
 * 최근 캠페인 목록 조회 (로그인 필수)
 */
export function fetchRecentCampaigns(opts: {
  days?: number;
  hasBounce?: boolean;
  channel?: string;
  limit?: number;
}): Promise<ApiResponse<RecentCampaignsData>> {
  const params = new URLSearchParams();
  if (opts.days !== undefined) params.set('days', String(opts.days));
  if (opts.hasBounce !== undefined) params.set('has_bounce', String(opts.hasBounce));
  if (opts.channel !== undefined) params.set('channel', opts.channel);
  if (opts.limit !== undefined) params.set('limit', String(opts.limit));

  return apiGet<RecentCampaignsData>(`/app/campaigns/recent?${params.toString()}`);
}

/**
 * 일시적 전송오류 재발송 요청 (로그인 필수)
 *
 * 응답 코드:
 * - OK: 재발송 시도 성공
 * - LIMIT_EXCEEDED: 3회 초과
 * - NOT_FOUND: 캠페인 없음
 * - FORBIDDEN: 본인 캠페인 아님
 * - INSUFFICIENT_BALANCE: 잔액 부족
 * - INVALID_INPUT: 재발송 대상 없음
 * - EXPIRED: 60일 경과
 */
export function requestResend(campaignId: string): Promise<ApiResponse<ResendData>> {
  return apiPost<ResendData>('/app/email/resend', {
    campaign_id: campaignId,
    resend_type: 'soft_bounce',
  });
}

/**
 * 상담원 이관 요청
 */
export interface EscalateRequest {
  intent: string;
  reason: string;
  team: string;
  priority: 'critical' | 'high' | 'medium' | 'low';
  collected_info?: Record<string, string>;
  conversation_history?: Array<{ role: string; content: string }>;
  anonymous_contact?: { name: string; email: string; phone: string };
}

export interface EscalateData {
  ticket_id: string;
  estimated_response_time: string;
  is_within_operating_hours: boolean;
  queue_position: number | null;
}

export function requestEscalation(req: EscalateRequest): Promise<ApiResponse<EscalateData>> {
  return apiPost<EscalateData>('/chatbot/api/escalate', req);
}

/**
 * 사용 분석 로그 전송
 */
export interface LogEvent {
  event: 'fallback' | 'message_sent' | 'session_start' | 'escalation';
  user_input?: string;
  auth_status: string;
  session_id: string;
  page: string;
}

export function sendLog(event: LogEvent): Promise<ApiResponse<null>> {
  return apiPost<null>('/chatbot/api/log', event);
}

/**
 * 대량 발송 견적 요청 (영업 리드)
 *
 * 비로그인 사용자가 챗봇 홈 CTA에서 4단계 폼을 완료하면 호출.
 * 영업팀에 알림이 전달되며, tier(standard/premium/enterprise)에 따라
 * 응대 우선순위가 달라진다.
 */
export interface QuoteRequest {
  channels: string[];
  volume: string;
  frequency: string;
  contact: {
    company: string;
    name: string;
    email: string;
    phone: string;
    message?: string;
  };
  consent_agreed: boolean;
  consent_at: string;
  session_id: string;
  source_page: string;
}

export interface QuoteResponse {
  quote_id: string;
  estimated_response: string;
  tier: 'standard' | 'premium' | 'enterprise';
  next_steps: string[];
}

export function submitQuoteRequest(
  req: QuoteRequest
): Promise<ApiResponse<QuoteResponse>> {
  return apiPost<QuoteResponse>('/chatbot/api/quote-request', req);
}

/**
 * 발송대행 신청 (영업 리드)
 *
 * 비로그인 사용자가 발송대행 소개 답변에서 [발송대행 신청] 버튼 클릭 시 노출되는
 * 단일 단계 폼의 제출. 채널/발송량/빈도 같은 견적 메타데이터는 받지 않으며,
 * 영업팀이 직접 컨설팅을 수행한다.
 */
export interface AgencyRequest {
  contact: {
    company: string;
    name: string;
    email: string;
    phone: string;
    message?: string;
  };
  consent_agreed: boolean;
  consent_at: string;
  session_id: string;
  source_page: string;
}

export interface AgencyResponse {
  request_id: string;
  estimated_response: string;
}

export function submitAgencyRequest(
  req: AgencyRequest
): Promise<ApiResponse<AgencyResponse>> {
  return apiPost<AgencyResponse>('/chatbot/api/agency-request', req);
}

/**
 * 프라이빗 견적 요청 (영업 리드)
 *
 * 비로그인 사용자가 프라이빗 카테고리에서 [프라이빗 견적 요청] 버튼 클릭 시 노출되는
 * 단일 단계 폼의 제출. 페이로드는 발송대행과 동일하나 destination이 다르다 —
 * 프라이빗 영업팀이 별도로 응대.
 */
export interface PrivateQuoteRequestPayload {
  contact: {
    company: string;
    name: string;
    email: string;
    phone: string;
    message?: string;
  };
  consent_agreed: boolean;
  consent_at: string;
  session_id: string;
  source_page: string;
}

export interface PrivateQuoteResponseData {
  request_id: string;
  estimated_response: string;
}

export function submitPrivateQuoteRequest(
  req: PrivateQuoteRequestPayload
): Promise<ApiResponse<PrivateQuoteResponseData>> {
  return apiPost<PrivateQuoteResponseData>('/chatbot/api/private-quote-request', req);
}

/**
 * 불편/오류/건의사항 신고 (로그인 필수)
 *
 * 로그인 세션에서 user 정보를 자동 추출하므로 페이로드에는 메시지·페이지·세션ID만 포함.
 * 응답 코드:
 * - OK: 신고 접수 성공
 * - AUTH_REQUIRED: 비로그인 (meta.login_url)
 * - INVALID_INPUT: 메시지 누락 / 길이 초과 / 안전하지 않은 입력
 */
export function submitFeedback(
  req: FeedbackRequestPayload
): Promise<ApiResponse<FeedbackData>> {
  return apiPost<FeedbackData>('/chatbot/api/feedback', req);
}
